/**
 * Profile Settings Component
 * 
 * Comprehensive user profile management with MongoDB integration
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUser, updateUser } from '../services/userService';

export default function ProfileSettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    username: '',
    job_title: '',
    avatar_url: '',
    bio: '',
    company_name: '',
    department: '',
    company_website: '',
    address: {
      street: '',
      city: '',
      postal_code: '',
      state: '',
      country: 'Germany'
    },
    social_links: {
      linkedin: '',
      github: '',
      twitter: '',
      website: ''
    },
    preferences: {
      language: 'English (US)',
      timezone: 'Europe/Berlin (GMT+1)',
      date_format: 'DD/MM/YYYY',
      currency: 'EUR (€)'
    },
    privacy_settings: {
      profile_public: true,
      show_email: false,
      show_activity: true,
      allow_indexing: true
    }
  });
  
  const [accountStats, setAccountStats] = useState({
    plan: 'Free',
    credits_remaining: 0,
    storage_used: 0,
    api_keys_count: 0,
    created_at: null
  });

  // Load user data on mount
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      
      setUserId(user.id);
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        job_title: user.job_title || '',
        avatar_url: user.avatar_url || '',
        bio: user.bio || '',
        company_name: user.company_name || '',
        department: user.department || '',
        company_website: user.company_website || '',
        address: user.address || {
          street: '',
          city: '',
          postal_code: '',
          state: '',
          country: 'Germany'
        },
        social_links: user.social_links || {
          linkedin: '',
          github: '',
          twitter: '',
          website: ''
        },
        preferences: user.preferences || {
          language: 'English (US)',
          timezone: 'Europe/Berlin (GMT+1)',
          date_format: 'DD/MM/YYYY',
          currency: 'EUR (€)'
        },
        privacy_settings: user.privacy_settings || {
          profile_public: true,
          show_email: false,
          show_activity: true,
          allow_indexing: true
        }
      });
      
      setAccountStats({
        plan: user.plan || 'Free',
        credits_remaining: user.credits_remaining || 0,
        storage_used: user.storage_used || 0,
        api_keys_count: user.api_keys_count || 0,
        created_at: user.created_at
      });
      
    } catch (error) {
      console.error('Error loading user data:', error);
      alert(t('profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      // Handle nested properties
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateUser(userId, formData);
      alert(t('profile.saveSuccess'));
      await loadUserData(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-dark-200">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-light-200/20 shadow-sm">
        <div className="px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div>
              <h1 className="text-2xl font-bold text-dark-300 flex items-center">
                <i className="fas fa-user-gear text-primary-500 mr-3"></i>
                {t('profile.title')}
              </h1>
              <p className="text-sm text-dark-100 mt-0.5">{t('profile.subtitle')}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button 
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-primary-dark to-primary-500 hover:opacity-90 text-white rounded-lg text-sm font-semibold transition-all shadow-md flex items-center disabled:opacity-50"
            >
              <i className="fas fa-floppy-disk mr-2"></i>
              {saving ? t('common.loading') : t('profile.saveChanges')}
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="px-8 flex space-x-6 border-t border-light-200/10">
          <button 
            onClick={() => setActiveTab('profile')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'profile' ? 'text-primary-500 border-primary-500' : 'text-dark-100 border-transparent hover:text-dark-300 hover:border-light-200/30'}`}
          >
            {t('settings.profile')}
          </button>
          <button 
            onClick={() => setActiveTab('security')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'security' ? 'text-primary-500 border-primary-500' : 'text-dark-100 border-transparent hover:text-dark-300 hover:border-light-200/30'}`}
          >
            {t('settings.security')}
          </button>
          <button 
            onClick={() => setActiveTab('preferences')}
            className={`px-1 py-3 text-sm font-medium border-b-2 ${activeTab === 'preferences' ? 'text-primary-500 border-primary-500' : 'text-dark-100 border-transparent hover:text-dark-300 hover:border-light-200/30'}`}
          >
            {t('profile.preferences')}
          </button>
        </div>
      </header>

      {/* Page Header Section */}
      <section className="bg-white border-b border-light-200/20 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-dark-300 mb-2">{t('profile.personalInfo')}</h2>
            <p className="text-dark-100">{t('profile.personalInfoDesc')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-light-200/20">
              <div className="text-xs text-dark-100 mb-0.5">{t('profile.memberSince')}</div>
              <div className="text-lg font-bold text-primary-500">{formatDate(accountStats.created_at)}</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-light-200/20">
              <div className="text-xs text-dark-100 mb-0.5">{t('profile.accountStatus')}</div>
              <div className="text-lg font-bold text-primary-dark">{t('profile.active')}</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-light-200/20">
              <div className="text-xs text-dark-100 mb-0.5">Plan</div>
              <div className="text-lg font-bold text-primary-500">{accountStats.plan}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Form Area */}
        <main className="flex-1 overflow-y-auto px-8 py-6">
          
          {activeTab === 'profile' && (
            <>
              {/* Profile Photo Section */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-camera text-primary-500 mr-2"></i>
                    Profile Photo
                  </h3>
                  
                  <div className="flex items-start space-x-8">
                    <div className="relative group">
                      <img 
                        src={formData.avatar_url || "https://ui-avatars.com/api/?name=" + formData.first_name + "+" + formData.last_name + "&size=128&background=00C6B3&color=fff"}
                        alt="Profile" 
                        className="w-32 h-32 rounded-2xl object-cover shadow-lg border-4 border-white"
                      />
                      <div className="absolute inset-0 bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                        <i className="fas fa-camera text-white text-2xl"></i>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-bold text-dark-300 mb-2">Change Profile Picture</h4>
                      <p className="text-sm text-dark-100 mb-4">Upload a new profile photo. Recommended size: 400x400px. Max file size: 5MB.</p>
                      
                      <div className="flex space-x-3">
                        <button className="px-6 py-2 bg-gradient-to-r from-primary-dark to-primary-500 hover:opacity-90 text-white rounded-lg font-semibold shadow-md transition-all flex items-center">
                          <i className="fas fa-upload mr-2"></i>
                          Upload Photo
                        </button>
                        <button className="px-6 py-2 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 rounded-lg font-semibold text-dark-300 transition-all">
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Basic Information */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-id-card text-primary-500 mr-2"></i>
                    Basic Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">First Name <span className="text-primary-500">*</span></span>
                      </label>
                      <input 
                        type="text" 
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Last Name <span className="text-primary-500">*</span></span>
                      </label>
                      <input 
                        type="text" 
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Email Address <span className="text-primary-500">*</span></span>
                      </label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Phone Number</span>
                      </label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Username</span>
                      </label>
                      <input 
                        type="text" 
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Job Title</span>
                      </label>
                      <input 
                        type="text" 
                        name="job_title"
                        value={formData.job_title}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Company Information */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-building text-primary-500 mr-2"></i>
                    Company Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Company Name</span>
                      </label>
                      <input 
                        type="text" 
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Department</span>
                      </label>
                      <input 
                        type="text" 
                        name="department"
                        value={formData.department}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Company Website</span>
                      </label>
                      <input 
                        type="url" 
                        name="company_website"
                        value={formData.company_website}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Address Section */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-location-dot text-primary-500 mr-2"></i>
                    Address Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="col-span-2">
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Street Address</span>
                      </label>
                      <input 
                        type="text" 
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">City</span>
                      </label>
                      <input 
                        type="text" 
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Postal Code</span>
                      </label>
                      <input 
                        type="text" 
                        name="address.postal_code"
                        value={formData.address.postal_code}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">State / Province</span>
                      </label>
                      <input 
                        type="text" 
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300"
                      />
                    </div>
                    
                    <div>
                      <label className="block mb-2">
                        <span className="text-sm font-bold text-dark-300">Country</span>
                      </label>
                      <select 
                        name="address.country"
                        value={formData.address.country}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 bg-white"
                      >
                        <option>Germany</option>
                        <option>Austria</option>
                        <option>Switzerland</option>
                        <option>United States</option>
                        <option>United Kingdom</option>
                      </select>
                    </div>
                  </div>
                </div>
              </section>

              {/* Bio & Social Links */}
              <section className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                  <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                    <i className="fas fa-user-pen text-primary-500 mr-2"></i>
                    Bio & Social Links
                  </h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block mb-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-dark-300">About Me</span>
                          <span className="text-xs text-dark-100">{formData.bio?.length || 0} / 500 characters</span>
                        </div>
                      </label>
                      <textarea 
                        rows="5" 
                        name="bio"
                        value={formData.bio}
                        onChange={handleChange}
                        maxLength="500"
                        placeholder="Tell us about yourself..."
                        className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">LinkedIn Profile</span>
                        </label>
                        <div className="relative">
                          <i className="fab fa-linkedin absolute left-4 top-4 text-light-200"></i>
                          <input 
                            type="url" 
                            name="social_links.linkedin"
                            value={formData.social_links.linkedin}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/..."
                            className="w-full pl-12 pr-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">GitHub Profile</span>
                        </label>
                        <div className="relative">
                          <i className="fab fa-github absolute left-4 top-4 text-light-200"></i>
                          <input 
                            type="url" 
                            name="social_links.github"
                            value={formData.social_links.github}
                            onChange={handleChange}
                            placeholder="https://github.com/..."
                            className="w-full pl-12 pr-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">Twitter / X</span>
                        </label>
                        <div className="relative">
                          <i className="fab fa-twitter absolute left-4 top-4 text-light-200"></i>
                          <input 
                            type="url" 
                            name="social_links.twitter"
                            value={formData.social_links.twitter}
                            onChange={handleChange}
                            placeholder="https://twitter.com/..."
                            className="w-full pl-12 pr-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block mb-2">
                          <span className="text-sm font-bold text-dark-300">Personal Website</span>
                        </label>
                        <div className="relative">
                          <i className="fas fa-globe absolute left-4 top-4 text-light-200"></i>
                          <input 
                            type="url" 
                            name="social_links.website"
                            value={formData.social_links.website}
                            onChange={handleChange}
                            placeholder="https://..."
                            className="w-full pl-12 pr-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}

          {activeTab === 'preferences' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                  <i className="fas fa-sliders text-primary-500 mr-2"></i>
                  Account Preferences
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-bold text-dark-300">Language</span>
                    </label>
                    <select 
                      name="preferences.language"
                      value={formData.preferences.language}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 bg-white"
                    >
                      <option>English (US)</option>
                      <option>Deutsch</option>
                      <option>Español</option>
                      <option>Français</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-bold text-dark-300">Timezone</span>
                    </label>
                    <select 
                      name="preferences.timezone"
                      value={formData.preferences.timezone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 bg-white"
                    >
                      <option>Europe/Berlin (GMT+1)</option>
                      <option>Europe/London (GMT+0)</option>
                      <option>America/New_York (GMT-5)</option>
                      <option>America/Los_Angeles (GMT-8)</option>
                      <option>Asia/Tokyo (GMT+9)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-bold text-dark-300">Date Format</span>
                    </label>
                    <select 
                      name="preferences.date_format"
                      value={formData.preferences.date_format}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 bg-white"
                    >
                      <option>DD/MM/YYYY</option>
                      <option>MM/DD/YYYY</option>
                      <option>YYYY-MM-DD</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block mb-2">
                      <span className="text-sm font-bold text-dark-300">Currency</span>
                    </label>
                    <select 
                      name="preferences.currency"
                      value={formData.preferences.currency}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 bg-white"
                    >
                      <option>EUR (€)</option>
                      <option>USD ($)</option>
                      <option>GBP (£)</option>
                      <option>CHF (Fr)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Privacy Settings */}
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8 mt-8">
                <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                  <i className="fas fa-shield-halved text-primary-500 mr-2"></i>
                  Privacy Settings
                </h3>
                
                <div className="space-y-5">
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">Make profile public</div>
                      <div className="text-xs text-dark-100 mt-1">Allow others to view your profile information</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        name="privacy_settings.profile_public"
                        checked={formData.privacy_settings.profile_public}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">Show email address</div>
                      <div className="text-xs text-dark-100 mt-1">Display email on your public profile</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        name="privacy_settings.show_email"
                        checked={formData.privacy_settings.show_email}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">Show activity status</div>
                      <div className="text-xs text-dark-100 mt-1">Let others see when you're active</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        name="privacy_settings.show_activity"
                        checked={formData.privacy_settings.show_activity}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-light-100 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-dark-300">Allow search engine indexing</div>
                      <div className="text-xs text-dark-100 mt-1">Let search engines find your profile</div>
                    </div>
                    <label className="relative inline-block w-12 h-6 ml-4">
                      <input 
                        type="checkbox" 
                        name="privacy_settings.allow_indexing"
                        checked={formData.privacy_settings.allow_indexing}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-light-200/30 peer-checked:bg-gradient-to-r peer-checked:from-primary-dark peer-checked:to-primary-500 rounded-full peer cursor-pointer transition-colors"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6"></div>
                    </label>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'security' && (
            <section className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
                <h3 className="text-lg font-bold text-dark-300 mb-6 flex items-center">
                  <i className="fas fa-shield-halved text-primary-500 mr-2"></i>
                  Security Settings
                </h3>
                
                <p className="text-dark-100 p-4 bg-light-100 rounded-lg">
                  <i className="fas fa-info-circle text-primary-500 mr-2"></i>
                  Authentication features will be available in the next update.
                </p>
              </div>
            </section>
          )}

          {/* Action Buttons */}
          <section className="mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-8">
              <div className="flex items-center justify-between">
                <div>
                  <button className="text-sm text-dark-100 hover:text-red-600 font-semibold flex items-center transition-colors">
                    <i className="fas fa-trash mr-2"></i>
                    Delete Account
                  </button>
                </div>
                
                <div className="flex space-x-3">
                  <button 
                    onClick={loadUserData}
                    className="px-8 py-3 bg-white border-2 border-light-200/30 hover:border-primary-500 hover:bg-light-100 rounded-lg font-semibold text-dark-300 transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSave}
                    disabled={saving}
                    className="px-8 py-3 bg-gradient-to-r from-primary-dark to-primary-500 hover:opacity-90 text-white rounded-lg font-semibold shadow-lg transition-all flex items-center disabled:opacity-50"
                  >
                    <i className="fas fa-check mr-2"></i>
                    {saving ? 'Saving...' : 'Save All Changes'}
                  </button>
                </div>
              </div>
            </div>
          </section>
          
        </main>

        {/* Right Sidebar */}
        <aside className="w-96 bg-white border-l border-light-200/20 overflow-y-auto">
          
          {/* Account Overview */}
          <section className="p-6 border-b border-light-200/20">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide">Account Overview</h3>
            
            <div className="space-y-4">
              {/* Credits box - hidden for now, will be relevant later */}
              <div className="hidden p-4 bg-gradient-to-br from-primary-dark to-primary-500 rounded-xl shadow-lg text-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-90">{accountStats.plan} Plan</span>
                  <i className="fas fa-crown text-xl"></i>
                </div>
                <div className="text-2xl font-bold mb-1">{accountStats.credits_remaining.toLocaleString()}</div>
                <div className="text-xs opacity-90">Credits Remaining</div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-light-100 rounded-lg">
                  <div className="flex items-center">
                    <i className="fas fa-calendar-check text-primary-500 mr-3"></i>
                    <span className="text-sm font-medium text-dark-300">Member Since</span>
                  </div>
                  <span className="text-sm font-bold text-primary-500">{formatDate(accountStats.created_at)}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-light-100 rounded-lg">
                  <div className="flex items-center">
                    <i className="fas fa-database text-primary-dark mr-3"></i>
                    <span className="text-sm font-medium text-dark-300">Storage Used</span>
                  </div>
                  <span className="text-sm font-bold text-primary-dark">{accountStats.storage_used} GB</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-light-100 rounded-lg">
                  <div className="flex items-center">
                    <i className="fas fa-key text-primary-500 mr-3"></i>
                    <span className="text-sm font-medium text-dark-300">API Keys</span>
                  </div>
                  <span className="text-sm font-bold text-primary-500">{accountStats.api_keys_count} Active</span>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="p-6 border-b border-light-200/20">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide">Quick Actions</h3>
            
            <div className="space-y-3">
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-download text-primary-500 mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Download My Data</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-file-export text-primary-dark mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Export Settings</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-clock-rotate-left text-primary-500 mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Activity Log</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-shield-halved text-primary-dark mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Security Settings</span>
              </button>
            </div>
          </section>

          {/* Tips */}
          <section className="p-6 border-b border-light-200/20">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide flex items-center">
              <i className="fas fa-lightbulb text-primary-500 mr-2"></i>
              Profile Tips
            </h3>
            
            <div className="space-y-3">
              <div className="p-3 bg-light-100 border-l-4 border-primary-500 rounded-r-lg">
                <p className="text-xs text-dark-100"><strong>Tip:</strong> Add a professional photo to increase profile visibility by 40%</p>
              </div>
              
              <div className="p-3 bg-light-100 border-l-4 border-primary-dark rounded-r-lg">
                <p className="text-xs text-dark-100"><strong>Tip:</strong> Complete your bio to help others understand your expertise</p>
              </div>
              
              <div className="p-3 bg-light-100 border-l-4 border-primary-500 rounded-r-lg">
                <p className="text-xs text-dark-100"><strong>Tip:</strong> Link your social profiles to build credibility</p>
              </div>
            </div>
          </section>

          {/* Support */}
          <section className="p-6">
            <h3 className="text-sm font-bold text-dark-300 mb-4 uppercase tracking-wide">Need Help?</h3>
            
            <div className="space-y-3">
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-book text-primary-500 mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Profile Guide</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-shield text-primary-dark mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Privacy Policy</span>
              </button>
              
              <button className="w-full p-3 bg-light-100 hover:bg-light-200/30 rounded-lg flex items-center transition-colors">
                <i className="fas fa-comments text-primary-500 mr-3"></i>
                <span className="text-sm font-medium text-dark-300">Contact Support</span>
              </button>
            </div>
          </section>
          
        </aside>
      </div>
    </div>
  );
}

